import moment from 'moment-timezone';
import { getMoneyStr } from '@/lib/moneyHelpers';
import DateHelper from './dateHelper';

export const STATUS = {
  1: 'Не активен',
  2: 'Активен',
  3: 'Заморожен',
  4: 'Окончен',
};

export const noop = () => {
};

const getCorrectWordByNumber = (input, forms) => {
  let wordNumber;

  if ((input >= 5 && input <= 20) || input === 11 || input === 0) {
    wordNumber = 2;
  } else {
    const mod = input % 10;
    if (mod === 1) {
      wordNumber = 0;
    } else if (mod >= 2 && mod <= 4) {
      wordNumber = 1;
    } else {
      wordNumber = 2;
    }
  }

  return forms[wordNumber];
};

const getReadablePeriod = (period) => {
  if (!period) {
    return 'бессрочный';
  }

  const words = {
    day: ['день', 'дня', 'дней'],
    month: ['месяц', 'месяца', 'месяцев'],
    year: ['год', 'года', 'лет'],
  };

  const [number, periodName] = String(period).split(' ');
  const word = getCorrectWordByNumber(number, words[periodName]);

  return `${number} ${word}`;
};

const getReadableVisitCount = (sub) => {
  const { type: subType, visitCount } = sub;
  let visitCountText = '';

  if (subType === 'limited' || (sub.type === 'dynamic' && sub.dynamicParams.dynamicPrice)) {
    const correctWord = getCorrectWordByNumber(visitCount, ['занятие', 'занятия', 'занятий']);
    visitCountText = `${visitCount} ${correctWord}`;
  } else if (subType === 'unlimited') {
    visitCountText = 'безлимитный';
  } else if (subType === 'dynamic') {
    visitCountText = 'плавающий';
  }

  return visitCountText;
};

const daysOfWeek = {
  mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0,
};

/**
   * Подсчитывает кол-во занятий для аб-та по заданным параметрам.
   * @param {string} beginDate - Дата начала действия аб-та в формате YYYY-MM-DD.
   * @param {string} endDate - Дата окончания действия аб-та в формате YYYY-MM-DD.
   * @param {string[]} weekdays - Дни недели, в которые могут проводиться занятия.
   *                   Формат - массив нужных дней недели, например ['sun', 'wed', 'fri'].
   * @param {{from: string, to: string}[]} holidays - Праздничные дни, которые не нужно учитывать.
   * @param {{freeze_start: string, freeze_end: string}[]} freezes - Массив заморозок.
   * @returns {number} - Кол-во занятий с учетом всех параметров.
   */
function calculateVisitCount({
  beginDate,
  endDate,
  weekdays = [],
  holidays = [],
  freezes = [],
}) {
  const weekdaysIndexes = weekdays.map(day => daysOfWeek[day]);

  const dateFrom = new Date(beginDate);
  const dateTo = new Date(endDate);

  // Получаем общее кол-во занятий в диапазоне в указанные дни недели
  // без учета выходных и заморозок.
  const totalLessons = DateHelper.countCertainDays(weekdaysIndexes, dateFrom, dateTo);

  // Получаем массив диапазонов дат, в которые занятия проводиться не должны,
  // объединив все периоды выходных и заморозок.
  const rangesToExclude = DateHelper.mergeDateRanges([
    ...DateHelper.formatDateRanges(holidays),
    ...DateHelper.formatDateRanges(freezes, 'freeze_start', 'freeze_end'),
  ]);

  // Получаем кол-во занятий в периодах, в которые занятия проводиться не должны.
  const lessonsToExclude = rangesToExclude.reduce((sum, range) => {
    // Определяем даты для подсчета уроков - берем пересечение исключаемого периода и основного.
    const rangeStart = range.from > dateFrom ? range.from : dateFrom;
    const rangeEnd = range.to < dateTo ? range.to : dateTo;

    // Если диапазон правильный, значит пересечение есть.
    if (rangeStart <= rangeEnd) {
      sum += DateHelper.countCertainDays(weekdaysIndexes, rangeStart, rangeEnd);
    }
    return sum;
  }, 0);

  // Из общего кол-ва занятий вычитаем исключения.
  return totalLessons - lessonsToExclude;
}

/** Обновляет цену и кол-во посещений для динамического абонемента */
export const updateDynamicSubscription = (subscription, filial = null) => {
  if (subscription.type !== 'dynamic' || !subscription.dynamicParams.dynamicPrice) {
    return subscription;
  }
  const nowDate = filial ? moment().tz(filial.timezone) : moment();
  let { beginDateSelectDefault = 'auto' } = subscription.onlinePayment;
  if (beginDateSelectDefault === 'auto') {
    beginDateSelectDefault = nowDate.date() < 15 ? 'fromThisMonth' : 'fromNextMonth';
  }
  let beginDate;
  switch (beginDateSelectDefault) {
    case 'fromNextMonth':
      beginDate = nowDate.add(1, 'month').startOf('month').format('YYYY-MM-DD');
      break;
    case 'fromThisMonth':
      beginDate = nowDate.startOf('month').format('YYYY-MM-DD');
      break;
    case 'fromNow':
    default:
      beginDate = nowDate.format('YYYY-MM-DD');
      break;
  }

  const [perValue, perUnit] = subscription.period.split(' ');
  const endDate = moment(beginDate)
    .add(+perValue, perUnit)
    .subtract(1, 'day')
    .format('YYYY-MM-DD');
  const { lessonsDays: weekdays = [], ignoreHolidays } = subscription.dynamicParams || {};
  let holidays = [];
  if (!ignoreHolidays && filial) {
    const filialHolidays = filial.holidays;
    holidays = filialHolidays;
  }
  const visitCount = calculateVisitCount({
    beginDate,
    endDate,
    weekdays,
    holidays,
  });
  subscription.visitCount = visitCount;
  subscription.price = visitCount * subscription.yield;
  return subscription;
};

/**
 * Возвращает абонемент для платежа.
 * Для динамического абонемента возвращает вместе с ценой и кол-вом посещений
 */
export const getSubscriptionForPayment = (subscription, filial = null) => {
  if (subscription.type !== 'dynamic' || !subscription.dynamicParams.dynamicPrice) {
    return subscription;
  }

  return updateDynamicSubscription({ ...subscription }, filial);
};

export const getDisplayName = (subToProcess, filial) => {
  const sub = getSubscriptionForPayment(subToProcess, filial);
  const { fieldsToShowInNameAsAdditionalInfo = [] } = sub;

  let displayName = sub.name;
  const fieldValues = fieldsToShowInNameAsAdditionalInfo.map((field) => {
    switch (field) {
      case 'price':
        return getMoneyStr(sub.price);
      case 'visitCount':
        return getReadableVisitCount(sub);
      case 'period':
        return getReadablePeriod(sub.period);
      default:
        return null;
    }
  }).filter(Boolean);

  if (fieldValues.length) {
    displayName += ` - ${fieldValues.join(', ')}`;
  }

  return displayName;
};